import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2,
  Loader2,
  AlertCircle,
  Share2,
  Maximize2,
  Instagram,
  Copy,
  Check,
} from "lucide-react";
import { Nav } from "../components/Nav";
import { colorById } from "../lib/colors";
import { shareOrDownload } from "../lib/share";
import { track, Events } from "../lib/analytics";
import { toast } from "sonner";
import { PageFooter } from "../components/PageFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INSTAGRAM_URL = "https://www.instagram.com/kisubikashop";

const Receipt = () => {
  const { id } = useParams();
  const [reservation, setReservation] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | ok | not-found
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reservations/${id}`);
        if (!cancelled) {
          setReservation(data);
          setPhase("ok");
        }
      } catch {
        if (!cancelled) setPhase("not-found");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleShare = async () => {
    if (!reservation || sharing) return;
    setSharing(true);
    const color = colorById(reservation.color_id);
    track(Events.ShareClicked, {
      source: "receipt",
      reservation_id: reservation.id,
      color_id: color.id,
    });
    const t = toast.loading("Composing your card…");
    try {
      const result = await shareOrDownload(color);
      toast.dismiss(t);
      track(Events.ShareCompleted, {
        source: "receipt",
        method: result.method,
      });
      if (result.method === "shared") toast.success("Shared. Tag @kisubikashop");
      else if (result.method === "downloaded")
        toast.success("Saved. Post it to your Story", {
          action: {
            label: "Open Instagram",
            onClick: () => window.open(INSTAGRAM_URL, "_blank", "noopener"),
          },
        });
    } catch {
      toast.dismiss(t);
      toast.error("Couldn’t generate share card");
    } finally {
      setSharing(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Receipt link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  if (phase === "loading") {
    return (
      <div className="min-h-screen bg-white">
        <Nav />
        <main className="pt-32 flex flex-col items-center justify-center text-center px-5">
          <Loader2 size={28} className="animate-spin kb-blue mb-4" />
          <div className="kb-mono-label kb-blue">FETCHING RECEIPT</div>
        </main>
      </div>
    );
  }

  if (phase === "not-found") {
    return (
      <div className="min-h-screen bg-white">
        <Nav />
        <main
          data-testid="receipt-not-found"
          className="pt-32 px-5 max-w-2xl mx-auto text-center pb-24"
        >
          <AlertCircle
            size={32}
            className="mx-auto mb-6"
            style={{ color: "var(--kb-error)" }}
          />
          <div className="kb-mono-label" style={{ color: "var(--kb-error)" }}>
            RECEIPT NOT FOUND
          </div>
          <h1 className="font-serif text-4xl mt-2 tracking-tight">
            We couldn’t find that reservation.
          </h1>
          <p className="text-[var(--kb-fg-2)] mt-3 font-light">
            Double-check the link or look up your bag by email.
          </p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link to="/reservations" className="kb-btn-primary">
              Look up by email
            </Link>
            <Link to="/shop" className="kb-btn-ghost">
              Back home
            </Link>
          </div>
        </main>
        <PageFooter />
      </div>
    );
  }

  const color = colorById(reservation.color_id);
  const isPaid = reservation.payment_status === "paid";
  const dateStr = new Date(reservation.created_at).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="min-h-screen bg-white text-[var(--kb-fg)]">
      <Nav />
      <main
        data-testid="receipt-page"
        className="pt-28 sm:pt-32 px-5 sm:px-10 lg:px-14 pb-24 max-w-5xl mx-auto"
      >
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left: visual */}
          <div className="lg:col-span-7 kb-anim-scale">
            <div className="relative aspect-square w-full overflow-hidden bg-[var(--kb-bg-2)] kb-grain">
              <div className="absolute top-4 left-4 kb-mono-label kb-blue z-10">
                {isPaid ? "RESERVED" : reservation.payment_status?.toUpperCase()}
              </div>
              <div className="absolute top-4 right-4 kb-mono-label text-[var(--kb-fg-2)] z-10">
                REF · {reservation.id.slice(0, 8).toUpperCase()}
              </div>
              <div
                className="kb-bag absolute inset-8 sm:inset-12"
                style={{ "--kb-tint": color.filter }}
              />
              <div
                className="absolute left-1/2 -translate-x-1/2 bottom-8 sm:bottom-12 w-2/3 h-3 rounded-full"
                style={{
                  background:
                    "radial-gradient(ellipse at center, rgba(10,10,10,0.18) 0%, rgba(10,10,10,0) 70%)",
                  filter: "blur(2px)",
                }}
              />
              <div className="absolute bottom-4 left-4 kb-mono-label text-[var(--kb-fg-2)]">
                LDN · 2026
              </div>
              <div className="absolute bottom-4 right-4 kb-mono-label kb-blue">
                {color.name}
              </div>
            </div>
          </div>

          {/* Right: details */}
          <aside className="lg:col-span-5 space-y-7 kb-anim-up kb-delay-2">
            <div>
              <div className="flex items-center gap-2 kb-mono-label kb-blue">
                <CheckCircle2 size={14} /> RESERVATION CONFIRMED
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[0.95] mt-3">
                {reservation.full_name?.split(" ")[0] || "Your"}’s
                <br />
                <span className="italic kb-blue">{color.name}</span>
              </h1>
              <p className="text-[var(--kb-fg-2)] font-light mt-3 max-w-md">
                Held against the next drop. Pay the balance only when your bag
                ships from London.
              </p>
            </div>

            <div className="border border-[var(--kb-fg)] divide-y divide-[var(--kb-border)]">
              <div className="px-5 py-3 flex items-center justify-between">
                <span className="kb-mono-label text-[var(--kb-fg-2)]">DEPOSIT</span>
                <span className="font-serif text-2xl">
                  £{Number(reservation.amount).toFixed(2)}
                </span>
              </div>
              <div className="px-5 py-3 flex items-center justify-between">
                <span className="kb-mono-label text-[var(--kb-fg-2)]">CURRENCY</span>
                <span className="kb-mono-label">
                  {reservation.currency?.toUpperCase()}
                </span>
              </div>
              <div className="px-5 py-3 flex items-center justify-between">
                <span className="kb-mono-label text-[var(--kb-fg-2)]">RESERVED</span>
                <span className="kb-mono-label">{dateStr}</span>
              </div>
              <div className="px-5 py-3 flex items-center justify-between">
                <span className="kb-mono-label text-[var(--kb-fg-2)]">EMAIL</span>
                <span className="kb-mono-label truncate ml-3">
                  {reservation.email}
                </span>
              </div>
              <div className="px-5 py-3 flex items-center justify-between">
                <span className="kb-mono-label text-[var(--kb-fg-2)]">
                  COLOUR · HEX
                </span>
                <div className="flex items-center gap-2">
                  <span
                    className="w-4 h-4 border border-[var(--kb-fg)]"
                    style={{ background: color.hex }}
                  />
                  <span className="kb-mono-label">{color.hex.toUpperCase()}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                data-testid="receipt-share"
                onClick={handleShare}
                disabled={sharing}
                className="kb-btn-primary"
              >
                {sharing ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Share2 size={14} />
                )}
                Share card
              </button>
              <button
                data-testid="receipt-copy"
                onClick={handleCopy}
                className="kb-btn-ghost"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? "Copied" : "Copy link"}
              </button>
              <Link
                to="/ar"
                state={{ colorId: color.id }}
                data-testid="receipt-ar"
                className="kb-btn-ghost"
              >
                <Maximize2 size={14} /> View in AR
              </Link>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="kb-btn-ghost"
              >
                <Instagram size={14} /> @kisubikashop
              </a>
            </div>

            <div className="kb-mono-label text-[var(--kb-fg-2)] pt-2 border-t border-[var(--kb-border)]">
              REF · {reservation.id.toUpperCase()}
            </div>
          </aside>
        </div>
      </main>
      <PageFooter />
    </div>
  );
};

export default Receipt;
